import React, { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import Button from 'components/button'
import { TOUR_STEP_STORAGE_KEY } from 'app-constants'

export interface TourStep {
  target: string
  content: string
  requiresInteraction?: boolean // If true, user must click the element (no Next button shown)
  dismissModalOnAdvance?: boolean // If true, dispatches Escape keypress when advancing to close open modals
}

interface ProductTourProps {
  steps: TourStep[]
  run: boolean
  onComplete: () => void
}

const PADDING = 8
const POLL_INTERVAL = 100
const POLL_TIMEOUT = 10000 // 10 seconds to allow for page navigation

// If the target is inside a popup/menu (positioned ancestor with z-index >= 1000),
// return the menu's rect so the tooltip positions outside the entire menu, not just the target.
/** A select's dropdown, whose options are part of picking its value: clicks in it count as on the target. */
const isSelectMenu = (el: HTMLElement): boolean => (el.id || '').includes('select')

function getContainerRect(target: Element): DOMRect {
  let el = target.parentElement
  while (el && el !== document.body) {
    const style = window.getComputedStyle(el)
    const zIndex = parseInt(style.zIndex, 10)
    const position = style.position
    if (!isNaN(zIndex) && zIndex >= 1000 && (position === 'absolute' || position === 'fixed')) {
      return el.getBoundingClientRect()
    }
    el = el.parentElement
  }
  return target.getBoundingClientRect()
}

function getTooltipPosition(targetRect: DOMRect, tooltipRect: DOMRect): { top: number; left: number } {
  const spaceBelow = window.innerHeight - targetRect.bottom
  const spaceAbove = targetRect.top
  const spaceRight = window.innerWidth - targetRect.right

  let top: number
  let left: number

  if (spaceBelow >= tooltipRect.height + 16) {
    top = targetRect.bottom + 12
    left = targetRect.left + targetRect.width / 2 - tooltipRect.width / 2
  } else if (spaceAbove >= tooltipRect.height + 16) {
    top = targetRect.top - tooltipRect.height - 12
    left = targetRect.left + targetRect.width / 2 - tooltipRect.width / 2
  } else if (spaceRight >= tooltipRect.width + 16) {
    top = targetRect.top + targetRect.height / 2 - tooltipRect.height / 2
    left = targetRect.right + 12
  } else {
    top = targetRect.top + targetRect.height / 2 - tooltipRect.height / 2
    left = targetRect.left - tooltipRect.width - 12
  }

  left = Math.max(8, Math.min(left, window.innerWidth - tooltipRect.width - 8))
  top = Math.max(8, Math.min(top, window.innerHeight - tooltipRect.height - 8))

  return { top, left }
}

const ProductTour: React.FC<ProductTourProps> = ({ steps, run, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(() => {
    // Restore current step from localStorage on initial mount
    if (!run) return 0
    const saved = localStorage.getItem(TOUR_STEP_STORAGE_KEY)
    if (saved) {
      const stepNum = parseInt(saved, 10)
      return !isNaN(stepNum) && stepNum >= 0 && stepNum < steps.length ? stepNum : 0
    }
    return 0
  })
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null)
  const [waiting, setWaiting] = useState(false)
  const [missingTarget, setMissingTarget] = useState(false)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const [tooltipPos, setTooltipPos] = useState<{ top: number; left: number } | null>(null)

  const step = steps[currentStep]
  const isLast = currentStep === steps.length - 1
  const [isInputTarget, setIsInputTarget] = useState(false)

  // Advance to the next step, dispatching Escape to close modals if the current step requires it.
  // The Escape is delayed so the target element's onClick handler runs first.
  const advanceStep = () => {
    if (step?.dismissModalOnAdvance) {
      setTimeout(() => {
        document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }))
      }, 500)
    }
    if (isLast) {
      localStorage.removeItem(TOUR_STEP_STORAGE_KEY)
      onComplete()
    } else {
      const nextStep = currentStep + 1
      localStorage.setItem(TOUR_STEP_STORAGE_KEY, nextStep.toString())
      setCurrentStep(nextStep)
    }
  }

  // Initialize step from localStorage when a new tour starts
  useEffect(() => {
    if (run && steps.length > 0) {
      const saved = localStorage.getItem(TOUR_STEP_STORAGE_KEY)
      if (saved) {
        const stepNum = parseInt(saved, 10)
        if (!isNaN(stepNum) && stepNum >= 0 && stepNum < steps.length) {
          setCurrentStep(stepNum)
        }
      }
    }
  }, [steps, run])

  // Save current step to localStorage whenever it changes
  useEffect(() => {
    if (run && currentStep >= 0) {
      localStorage.setItem(TOUR_STEP_STORAGE_KEY, currentStep.toString())
    }
  }, [currentStep, run])

  // Mark the page while a tour runs, so controls that normally show only on
  // hover (a column's ⋮) stay visible for the tour to point at.
  useEffect(() => {
    if (!run) return
    document.body.setAttribute('data-tour-running', '')
    return () => document.body.removeAttribute('data-tour-running')
  }, [run])

  // Find target element, poll if not found yet
  useEffect(() => {
    if (!run || !step) return

    setMissingTarget(false)
    setTargetRect(null)
    setTooltipPos(null)
    const el = document.querySelector(step.target)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      setTargetRect(el.getBoundingClientRect())
      setIsInputTarget(el.tagName === 'INPUT' || el.tagName === 'TEXTAREA')
      setWaiting(false)
      return
    }

    // Element not in DOM yet — poll for it
    setTargetRect(null)
    setWaiting(true)
    const start = Date.now()
    const interval = setInterval(() => {
      const found = document.querySelector(step.target)
      if (found) {
        clearInterval(interval)
        found.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        setMissingTarget(false)
        setTargetRect(found.getBoundingClientRect())
        setIsInputTarget(found.tagName === 'INPUT' || found.tagName === 'TEXTAREA')
        setWaiting(false)
      } else if (Date.now() - start > POLL_TIMEOUT) {
        clearInterval(interval)
        setMissingTarget(true)
        setWaiting(false)
      }
    }, POLL_INTERVAL)

    return () => clearInterval(interval)
  }, [run, step, currentStep])

  // Update tooltip position after targetRect changes
  // Uses the container rect (e.g. parent menu) and any open portaled menus
  // so the tooltip doesn't overlap popups or dropdown options.
  useEffect(() => {
    if (!targetRect || !tooltipRef.current || !step) return
    const el = document.querySelector(step.target)
    const containerRect = el ? getContainerRect(el) : targetRect

    // Union with any boosted portaled menus so tooltip avoids them
    let top = containerRect.top
    let left = containerRect.left
    let bottom = containerRect.bottom
    let right = containerRect.right
    boostedElementsRef.current.forEach((_, menuEl) => {
      const r = menuEl.getBoundingClientRect()
      if (r.width > 0 && r.height > 0) {
        top = Math.min(top, r.top)
        left = Math.min(left, r.left)
        bottom = Math.max(bottom, r.bottom)
        right = Math.max(right, r.right)
      }
    })
    const positioningRect = new DOMRect(left, top, right - left, bottom - top)

    const tooltipRect = tooltipRef.current.getBoundingClientRect()
    setTooltipPos(getTooltipPosition(positioningRect, tooltipRect))
  }, [targetRect, step])

  // Reposition on resize/scroll
  useEffect(() => {
    if (!run || !step) return
    const handleReposition = () => {
      const el = document.querySelector(step.target)
      if (el) setTargetRect(el.getBoundingClientRect())
    }
    window.addEventListener('resize', handleReposition)
    window.addEventListener('scroll', handleReposition, true)
    return () => {
      window.removeEventListener('resize', handleReposition)
      window.removeEventListener('scroll', handleReposition, true)
    }
  }, [run, step])

  // Boost z-index of any open portaled menus (rendered to #root or body) above the overlay
  // so they remain visible: select dropdowns, and any menu holding the step's target (a step
  // that points at an entry in a menu an earlier step opened, such as the sidebar's ⋯ or +).
  // Menus opened during a tour step are assumed to be intentional.
  // Also triggers a targetRect refresh so the tooltip repositions around the new menu.
  const boostedElementsRef = useRef<Map<HTMLElement, string>>(new Map())
  useEffect(() => {
    if (!run) return

    const boostMenus = () => {
      const root = document.getElementById('root') || document.body
      const portaled = root.querySelectorAll(':scope > [style*="z-index"]')
      let boostedNew = false
      for (let i = 0; i < portaled.length; i++) {
        const el = portaled[i] as HTMLElement
        const z = parseInt(el.style.zIndex, 10)
        const holdsTarget = step ? el.querySelector(step.target) !== null : false
        if (!isNaN(z) && z >= 1000 && z < 10000 && (isSelectMenu(el) || holdsTarget)) {
          if (!boostedElementsRef.current.has(el)) {
            boostedElementsRef.current.set(el, el.style.zIndex)
            boostedNew = true
          }
          el.style.zIndex = '10001'
        }
      }
      // Trigger tooltip reposition when a new menu appears
      if (boostedNew && step) {
        const el = document.querySelector(step.target)
        if (el) setTargetRect(el.getBoundingClientRect())
      }
    }

    // Boost any already-open menus
    boostMenus()

    // Watch for new portaled menus appearing
    const root = document.getElementById('root') || document.body
    const observer = new MutationObserver(boostMenus)
    observer.observe(root, { childList: true })

    return () => {
      observer.disconnect()
      // Restore original z-index values
      boostedElementsRef.current.forEach((originalZ, el) => {
        el.style.zIndex = originalZ
      })
      boostedElementsRef.current.clear()
    }
  }, [run, currentStep])

  // Auto-advance when the spotlit element is clicked (only for interactive steps).
  // Uses a document-level capture listener so it works even if React re-renders
  // and swaps out the target DOM node between effect runs.
  useEffect(() => {
    if (!run || !step || !targetRect || isInputTarget) return
    if (!step.requiresInteraction) return

    const handleTargetClick = (e: MouseEvent) => {
      const clicked = e.target as HTMLElement
      const el = document.querySelector(step.target)
      if (!el) return

      // Check if click was on the target element or inside it
      let isTargetClick = el.contains(clicked) || el === clicked

      // Also check if click was inside a boosted portaled select menu,
      // since dropdown options are rendered via portal outside the target element.
      // Not any other boosted menu: one lifted because it holds the target (the sidebar's ⋯)
      // offers other entries, and choosing one of them isn't the step.
      if (!isTargetClick) {
        boostedElementsRef.current.forEach((_, menuEl) => {
          if (isSelectMenu(menuEl) && menuEl.contains(clicked)) isTargetClick = true
        })
      }

      if (!isTargetClick) return

      advanceStep()
    }

    document.addEventListener('click', handleTargetClick, true)
    return () => document.removeEventListener('click', handleTargetClick, true)
  }, [run, step, targetRect, isLast, onComplete, isInputTarget, advanceStep])

  // Reset on stop and clear localStorage (only when transitioning from running to stopped)
  const wasRunning = useRef(false)
  useEffect(() => {
    if (run) {
      wasRunning.current = true
    } else if (wasRunning.current) {
      wasRunning.current = false
      setCurrentStep(0)
      setTargetRect(null)
      setTooltipPos(null)
      setWaiting(false)
      setMissingTarget(false)
      localStorage.removeItem(TOUR_STEP_STORAGE_KEY)
    }
  }, [run])

  if (!run || !step || waiting || (!targetRect && !missingTarget)) return null

  // Normal mode: target found, show spotlight
  // Degraded mode: target not found, show centered tooltip with full dimmer
  const hasCutout = targetRect && !missingTarget

  const cutout = hasCutout
    ? {
        x: targetRect.x - PADDING,
        y: targetRect.y - PADDING,
        width: targetRect.width + PADDING * 2,
        height: targetRect.height + PADDING * 2
      }
    : null

  const handleDismiss = () => {
    localStorage.removeItem(TOUR_STEP_STORAGE_KEY)
    onComplete()
  }

  // Prevent clicks on the overlay from propagating to document-level handlers
  // (e.g. Menu's click-outside listener), so open dropdowns stay open.
  const stopPropagation = (e: React.MouseEvent) => e.stopPropagation()

  // Build an SVG path with evenodd fill rule: large outer rect + inner cutout rect.
  // The evenodd rule punches a hole where the cutout is, so clicks pass through
  // to the underlying element while the rest of the overlay blocks interaction.
  const r = 8
  const overlayClipPath = cutout
    ? (() => {
        const { x, y, width: w, height: h } = cutout
        return `path(evenodd, "M 0 0 H 10000 V 10000 H 0 Z M ${x + r} ${y} H ${x + w - r} A ${r} ${r} 0 0 1 ${x + w} ${y + r} V ${y + h - r} A ${r} ${r} 0 0 1 ${x + w - r} ${y + h} H ${x + r} A ${r} ${r} 0 0 1 ${x} ${y + h - r} V ${y + r} A ${r} ${r} 0 0 1 ${x + r} ${y} Z")`
      })()
    : undefined

  return createPortal(
    <>
      <div
        className="fixed inset-0 bg-black/50 dark:bg-black/70 z-[10000]"
        onMouseDown={stopPropagation}
        style={overlayClipPath ? { clipPath: overlayClipPath } : undefined}
      />
      <div
        ref={tooltipRef}
        className="fixed z-[10001] bg-surface rounded-xl p-4 shadow-[0_4px_20px_rgba(0,0,0,0.15)] max-w-[320px] pointer-events-auto"
        style={
          missingTarget
            ? { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }
            : {
                top: tooltipPos?.top ?? 0,
                left: tooltipPos?.left ?? 0,
                visibility: tooltipPos ? 'visible' : 'hidden'
              }
        }
      >
        <button
          className="absolute top-2 right-2 bg-transparent border-none text-lg leading-none text-[var(--color-muted)] cursor-pointer px-1.5 py-0.5 rounded hover:text-[var(--color-body)] hover:bg-[var(--color-surface-sunken)]"
          onClick={handleDismiss}
          aria-label="Close tour"
        >
          &times;
        </button>
        <div className="text-sm leading-relaxed text-[var(--color-body)] mb-3">{step.content}</div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-[var(--color-muted)]">
            {currentStep + 1} of {steps.length}
          </span>
          <div className="flex gap-2">
            {missingTarget ? (
              <Button internalType="outline" className="text-xs" onClick={isLast ? handleDismiss : advanceStep}>
                {isLast ? 'Done' : 'Next'}
              </Button>
            ) : isLast && !step.requiresInteraction ? (
              <Button internalType="outline" className="text-xs" onClick={handleDismiss}>
                Done
              </Button>
            ) : !isLast && (isInputTarget || !step.requiresInteraction) ? (
              <Button internalType="outline" className="text-xs" onClick={advanceStep}>
                Next
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </>,
    document.body
  )
}

export default ProductTour
